import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FranchiseSalesOrganizationForm } from '@/components/forms/profile/FranchiseSalesOrganizationForm';
import { IndustryBackgroundForm } from '@/components/forms/profile/IndustryBackgroundForm';
import { FranchiseSalesOrganizationFormSchema } from '@/components/forms/schemas/FranchiseSalesOrganizationFormSchema';
import { Button } from '@/components/ui/button';
import { useCreateConsultantIndustries } from '@/hooks/consultant-industries/useCreateConsultantIndustries';
import { useGetConsultantIndustries } from '@/hooks/consultant-industries/useGetConsultantIndustries';
import { useUpdateConsultantIndustries } from '@/hooks/consultant-industries/useUpdateConsultantIndustries';
import { toast } from '@/hooks/ui/use-toast';
import { useConsultantStore } from '@/stores/useConsultantStore';

type FranchiseSalesOrganizationFormValues = z.infer<
  typeof FranchiseSalesOrganizationFormSchema
>;

interface IndustriesTabProps {
  setIsDirty: (isDirty: boolean) => void;
}

export const IndustriesTab = ({ setIsDirty }: IndustriesTabProps) => {
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const franchiseSalesOrganizationForm =
    useForm<FranchiseSalesOrganizationFormValues>({
      resolver: zodResolver(FranchiseSalesOrganizationFormSchema),
      defaultValues: {},
    });

  const { consultantIndustries, setConsultantIndustries } =
    useConsultantStore();

  const {
    result: consultantIndustriesResult,
    isError: isConsultantIndustriesError,
    isSuccess: isConsultantIndustriesSuccess,
    isLoading: isConsultantIndustriesLoading,
  } = useGetConsultantIndustries();
  const {
    mutate: createConsultantIndustries,
    result: createConsultantIndustriesResult,
    isError: isCreateConsultantIndustriesError,
    isSuccess: isCreateConsultantIndustriesSuccess,
    isLoading: isCreateConsultantIndustriesLoading,
  } = useCreateConsultantIndustries();
  const {
    mutate: updateConsultantIndustries,
    result: updateConsultantIndustriesResult,
    isError: isUpdateConsultantIndustriesError,
    isSuccess: isUpdateConsultantIndustriesSuccess,
    isLoading: isUpdateConsultantIndustriesLoading,
  } = useUpdateConsultantIndustries();

  const resetForms = useCallback(() => {
    franchiseSalesOrganizationForm.reset({
      ...consultantIndustries,
    });

    setSelectedIndustries([
      ...(consultantIndustries?.industry_background || []),
    ]);
  }, [consultantIndustries]);

  const handleSubmit = async (): Promise<void> => {
    const isFranchiseSalesOrganizationFormValid =
      await franchiseSalesOrganizationForm.trigger();

    if (isFranchiseSalesOrganizationFormValid) {
      const submitPayload = {
        ...franchiseSalesOrganizationForm.getValues(),
        industry_background: selectedIndustries,
      };

      if (!consultantIndustries) {
        createConsultantIndustries(submitPayload);
      } else {
        updateConsultantIndustries(submitPayload);
      }
    }
  };

  const renderEditButtons = () => {
    return (
      <div className="flex w-full justify-end">
        <div className="flex gap-1">
          <Button size="sm" className="w-full md:w-fit" onClick={handleSubmit}>
            Save Change
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setIsDirty(franchiseSalesOrganizationForm.formState.isDirty);
  }, [franchiseSalesOrganizationForm.formState.isDirty, setIsDirty]);

  useEffect(() => {
    if (isConsultantIndustriesError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isConsultantIndustriesError]);

  useEffect(() => {
    if (isConsultantIndustriesSuccess && consultantIndustriesResult?.data) {
      if (consultantIndustriesResult?.data) {
        setConsultantIndustries(consultantIndustriesResult?.data);
      }

      resetForms();
    }
  }, [
    consultantIndustriesResult,
    isConsultantIndustriesSuccess,
    resetForms,
    setConsultantIndustries,
  ]);

  useEffect(() => {
    if (
      isCreateConsultantIndustriesError ||
      isUpdateConsultantIndustriesError
    ) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isCreateConsultantIndustriesError, isUpdateConsultantIndustriesError]);

  useEffect(() => {
    if (
      isCreateConsultantIndustriesSuccess ||
      isUpdateConsultantIndustriesSuccess
    ) {
      if (createConsultantIndustriesResult?.data) {
        setConsultantIndustries(createConsultantIndustriesResult?.data);
      }

      if (updateConsultantIndustriesResult?.data) {
        setConsultantIndustries(updateConsultantIndustriesResult?.data);
      }

      resetForms();
    }
  }, [
    createConsultantIndustriesResult,
    updateConsultantIndustriesResult,
    isCreateConsultantIndustriesSuccess,
    isUpdateConsultantIndustriesSuccess,
    resetForms,
    setConsultantIndustries,
  ]);

  // no parent auto-save registration; manual save only

  if (isConsultantIndustriesLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      {(isCreateConsultantIndustriesLoading ||
        isUpdateConsultantIndustriesLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <IndustryBackgroundForm
        selectedIndustries={selectedIndustries}
        setSelectedIndustries={setSelectedIndustries}
      />
      <FranchiseSalesOrganizationForm form={franchiseSalesOrganizationForm} />
      {renderEditButtons()}
    </div>
  );
};
